import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout, AuthStatus } from '../components/AuthLayout';
import { InlineError } from '../components/Feedback';
import { TextField } from '../components/Form';
import { account } from '../lib/endpoints';
import { ApiError, errorMessage } from '../lib/api';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!EMAIL_PATTERN.test(email.trim())) {
      setError('Enter the email address you sign in with.');
      return;
    }

    setPending(true);
    setError(null);
    try {
      await account.forgotPassword(email.trim());
      setSent(true);
    } catch (cause) {
      // The endpoint always accepts a well-formed request, so only transport-level failures land
      // here. Anything the server answered with is still shown as the same neutral confirmation,
      // otherwise the screen would leak whether the address exists.
      if (cause instanceof ApiError && cause.status >= 400 && cause.status < 500) {
        setSent(true);
      } else {
        setError(errorMessage(cause));
      }
    } finally {
      setPending(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle="Password reset requested."
        footer={
          <>
            <Link to="/signin">Back to sign in</Link>
            <button type="button" className="link-button" onClick={() => setSent(false)}>
              Try another address
            </button>
          </>
        }
        note="For your security we never say whether an address has an account."
      >
        <AuthStatus tone="info" title="If that address has an account, a reset link is on its way">
          <p>
            Open the link within the next hour to choose a new password. It works only once.
          </p>
          <p className="auth-status-hint">
            Nothing after a couple of minutes? Check your spam folder, then try again.
          </p>
        </AuthStatus>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We will email you a link to set a new one."
      footer={
        <>
          <Link to="/signin">Back to sign in</Link>
          <Link to="/signup">Create an account</Link>
        </>
      }
    >
      <form className="signin-form" onSubmit={onSubmit} noValidate>
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          required
          autoComplete="username"
          placeholder="you@company.com"
        />

        <InlineError message={error} />

        <button type="submit" className="btn btn-primary btn-block" disabled={pending}>
          {pending ? 'Sending…' : 'Email me a reset link'}
        </button>
      </form>
    </AuthLayout>
  );
}
